import {ContenitoreFormato} from '../_models/ContenitoreFormato';

// Validazione rigorosa del codice contenitore digitato a mano (issue #3594, feature 144).
// Codice generato: {yy}{mese A–L}{testo intermedio}{progressivo}. Un codice con prefisso data deve
// rispettare interamente il default o uno dei formati dell'organizzazione; i codici senza prefisso
// (etichette esterne, codici storici) passano. Il gemello lato backend è
// BackEnd/ArchiConnectLocalApi/src/Archimedia.ArchiConnect.LocalApi.Domain/Helpers/CodiceContenitoreFormato.cs
// (IsCodiceConforme, DescriviFormato): regola e messaggio devono restare allineati.

export interface EsitoValidazioneCodice {
  valido: boolean;
  // Valorizzato solo se valido === false.
  messaggio?: string;
}

const PREFISSO_LENGTH = 3;
const PREFISSO_DATA = /^\d{2}[A-L]/i;
const PROGRESSIVO_DEFAULT_LENGTH = 10;
const MIDDLE_DEFAULT_LENGTH = 6;
const MIDDLE_ANAGRAFICA = 1;
const MIDDLE_RIF_ODP = 2;
const MIDDLE_TESTO_FISSO = 3;

const FORMATO_DEFAULT: Partial<ContenitoreFormato> = {padChar: '0', padCharCount: PROGRESSIVO_DEFAULT_LENGTH, middleTextType: 0};

// testoFisso valorizzato → confronto case-insensitive; altrimenti, se lunghezza > 0, middle numerico.
interface VarianteMiddle {
  lunghezza: number;
  testoFisso?: string;
}

interface Layout {
  padChar: string;
  progressivoLength: number;
  middleLength: number;
  varianti: VarianteMiddle[];
}

// Toglie i padChar iniziali; il resto deve essere di sole cifre. Solo padChar → valido (zero).
export function isProgressivoValido(segmento: string, padChar: string): boolean {
  if (!segmento) return false;
  const pad = padChar.toUpperCase();
  let i = 0;
  while (i < segmento.length && segmento[i].toUpperCase() === pad) i++;
  return /^\d*$/.test(segmento.substring(i));
}

function layoutDi(formato: Partial<ContenitoreFormato>): Layout {
  const progressivoLength = formato.padCharCount ?? PROGRESSIVO_DEFAULT_LENGTH;
  const middleLength = formato.middleTextCount ?? formato.padCharCount ?? MIDDLE_DEFAULT_LENGTH;
  return {
    padChar: formato.padChar ? formato.padChar[0] : '0',
    progressivoLength,
    middleLength,
    varianti: variantiDi(formato, middleLength),
  };
}

// Stesse regole della generazione (ContenitoriService.CheckFormatoCodice): il middle 1/2 resta vuoto
// quando mancano anagrafica/ordine; il testo fisso vuoto equivale a nessun middle.
function variantiDi(formato: Partial<ContenitoreFormato>, middleLength: number): VarianteMiddle[] {
  const tipo = formato.middleTextType;
  if (tipo === MIDDLE_ANAGRAFICA || tipo === MIDDLE_RIF_ODP) return [{lunghezza: 0}, {lunghezza: middleLength}];
  if (tipo === MIDDLE_TESTO_FISSO && formato.middleText) {
    return [{lunghezza: formato.middleText.length, testoFisso: formato.middleText}];
  }
  return [{lunghezza: 0}];
}

function isMiddleValido(middle: string, variante: VarianteMiddle, padChar: string): boolean {
  if (variante.testoFisso !== undefined) return middle.toUpperCase() === variante.testoFisso.toUpperCase();
  return middle.length === 0 || isProgressivoValido(middle, padChar);
}

function isConformeAVariante(resto: string, variante: VarianteMiddle, layout: Layout): boolean {
  if (resto.length !== variante.lunghezza + layout.progressivoLength) return false;
  const middle = resto.substring(0, variante.lunghezza);
  return isMiddleValido(middle, variante, layout.padChar) &&
    isProgressivoValido(resto.substring(variante.lunghezza), layout.padChar);
}

function isConforme(codice: string, formato: Partial<ContenitoreFormato>): boolean {
  if (!PREFISSO_DATA.test(codice)) return true;
  const layout = layoutDi(formato);
  const resto = codice.substring(PREFISSO_LENGTH);
  return layout.varianti.some((variante) => isConformeAVariante(resto, variante, layout));
}

function descriviMiddle(formato: Partial<ContenitoreFormato>, layout: Layout): string {
  switch (formato.middleTextType) {
    case MIDDLE_ANAGRAFICA:
      return ` + [anagrafica di ${layout.middleLength} caratteri]`;
    case MIDDLE_RIF_ODP:
      return ` + [ordine di produzione di ${layout.middleLength} caratteri]`;
    case MIDDLE_TESTO_FISSO:
      return formato.middleText ? ` + ${formato.middleText.toUpperCase()}` : '';
    default:
      return '';
  }
}

// Stesso testo di CodiceContenitoreFormato.DescriviFormato lato C#.
export function descriviFormatoContenitore(formato?: ContenitoreFormato | null): string {
  const effettivo = formato ?? FORMATO_DEFAULT;
  const layout = layoutDi(effettivo);
  const progressivo = layout.padChar === '0'
    ? `${layout.progressivoLength} cifre`
    : `${layout.progressivoLength} caratteri (cifre precedute da '${layout.padChar}')`;
  return `Codice non valido: formato atteso AA + lettera mese (A–L)${descriviMiddle(effettivo, layout)} + ${progressivo}`;
}

// Valida il codice su un solo formato (default se assente). Il valore non viene trasformato.
export function validaCodiceContenitore(codice: string, formato?: ContenitoreFormato | null): EsitoValidazioneCodice {
  if (isConforme(codice ?? '', formato ?? FORMATO_DEFAULT)) return {valido: true};
  return {valido: false, messaggio: descriviFormatoContenitore(formato)};
}

// Unione: valido se conforme al default O a uno qualsiasi dei formati, qualunque sia il formato del
// campo. Il formato del campo (default se assente o id 0) serve solo al messaggio di rifiuto.
export function validaCodiceSuFormati(codice: string, formati: ContenitoreFormato[],
                                      formatoCampo?: ContenitoreFormato | null): EsitoValidazioneCodice {
  const valore = codice ?? '';
  const ammessi: Partial<ContenitoreFormato>[] = [FORMATO_DEFAULT, ...(formati ?? [])];
  if (ammessi.some((formato) => isConforme(valore, formato))) return {valido: true};
  const perMessaggio = formatoCampo && formatoCampo.id ? formatoCampo : null;
  return {valido: false, messaggio: descriviFormatoContenitore(perMessaggio)};
}
